import { LayoutDashboard, Users, FileText, Settings, ClipboardList, LogOut, Menu, X, Bell, MapPin } from "lucide-react";
import { cn } from "@/lib/utils";
import { Link, useLocation } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useState } from "react";
import { useIsMobile } from "@/hooks/use-mobile";
import { useNotifications } from "@/hooks/useNotifications";
import { useBranchContext } from "@/hooks/useBranchContext";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const navItems = [
  { id: "dashboard", label: "Dashboard", icon: LayoutDashboard, path: "/" },
  { id: "patients", label: "Patients", icon: Users, path: "/patients" },
  { id: "my-tasks", label: "My Tasks", icon: ClipboardList, path: "/my-tasks" },
  { id: "reports", label: "Reports", icon: FileText, path: "/reports" },
  { id: "settings", label: "Settings", icon: Settings, path: "/settings" },
];

export const Navbar = () => {
  const location = useLocation();
  const { signOut } = useAuth();
  const isMobile = useIsMobile();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { unreadCount } = useNotifications();
  const {
    branches,
    currentBranchId,
    setCurrentBranchId,
    isAdmin: branchIsAdmin,
    userBranchId,
    assignedBranchIds,
    isLoading: branchesLoading,
    assignedLoading,
    authLoading,
  } = useBranchContext();

  const availableBranches = branchIsAdmin
    ? branches
    : branches.filter((b) =>
        (assignedBranchIds.length > 0 ? assignedBranchIds : userBranchId != null ? [userBranchId] : []).includes(b.id)
      );
  const isCurrentBranchValid =
    currentBranchId != null && availableBranches.some((b) => b.id === currentBranchId);

  return (
    <nav className="bg-card border-b border-border">
      <div className="mx-auto px-2 sm:px-3">
        <div className="flex items-center justify-between h-14 sm:h-16">
          <div className="flex items-center flex-1">
            {/* Logo */}
            <div className="flex-shrink-0 mr-4 sm:mr-6">
              <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-primary flex items-center justify-center">
                <div className="w-5 h-5 sm:w-6 sm:h-6 rounded-full border-2 border-primary-foreground" />
              </div>
            </div>

            {/* Desktop Navigation */}
            {!isMobile && (
              <div className="flex items-center space-x-1">
                {navItems.map((item) => {
                  const Icon = item.icon;
                  const isActive = location.pathname === item.path;

                  return (
                  <Link
                      key={item.id}
                      to={item.path}
                      className={cn(
                        "flex items-center space-x-2 px-1.5 py-1 rounded-md text-sm font-medium transition-colors min-h-[44px]",
                        isActive
                          ? "text-primary bg-primary/10"
                          : "text-muted-foreground hover:text-foreground hover:bg-muted",
                      )}
                    >
                      <Icon className="w-4 h-4" />
                      <span>{item.label}</span>
                    </Link>
                  );
                })}
              </div>
            )}
          </div>

          {/* Desktop Right Actions */}
          {!isMobile && (
            <div className="flex items-center space-x-2">
              <div className="flex items-center gap-2 min-w-[220px]">
                <MapPin className="w-4 h-4 text-muted-foreground" />
                <Select
                  value={isCurrentBranchValid ? String(currentBranchId) : undefined}
                  onValueChange={(value) => setCurrentBranchId(Number(value))}
                  disabled={branchesLoading || authLoading || (availableBranches.length === 0 && !assignedLoading)}
                >
                  <SelectTrigger className="h-9">
                    <SelectValue placeholder={branchesLoading || authLoading || (assignedLoading && !branchIsAdmin) ? "Loading..." : "Select branch"} />
                  </SelectTrigger>
                  <SelectContent>
                    {availableBranches.map((branch) => (
                      <SelectItem key={branch.id} value={String(branch.id)}>
                        {branch.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <Link to="/notifications" className="relative">
                <Button
                  variant="ghost"
                  size="icon"
                  className="min-h-[44px] min-w-[44px]"
                >
                  <Bell className="w-5 h-5" />
                </Button>
                {unreadCount > 0 && (
                  <Badge 
                    variant="destructive" 
                    className="absolute -top-1 -right-1 h-5 w-5 flex items-center justify-center p-0 text-[10px] rounded-full"
                  >
                    {unreadCount > 99 ? "99+" : unreadCount}
                  </Badge>
                )}
              </Link>
              <Button
                variant="ghost"
                size="icon"
                onClick={signOut}
                className="min-h-[44px] min-w-[44px]"
              >
                <LogOut className="w-5 h-5" />
              </Button>
            </div>
          )}

          {/* Mobile Menu Button */}
          {isMobile && (
            <Button
              variant="ghost"
              size="default"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="min-h-[44px] min-w-[44px] p-1"
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </Button>
          )}
        </div>

        {/* Mobile Menu */}
        {isMobile && mobileMenuOpen && (
          <div className="py-2 space-y-1 border-t border-border">
            <div className="px-2 py-2">
              <div className="flex items-center gap-2">
                <MapPin className="w-4 h-4 text-muted-foreground" />
                <Select
                  value={isCurrentBranchValid ? String(currentBranchId) : undefined}
                  onValueChange={(value) => setCurrentBranchId(Number(value))}
                  disabled={branchesLoading || authLoading || (availableBranches.length === 0 && !assignedLoading)}
                >
                  <SelectTrigger className="h-9 w-full">
                    <SelectValue placeholder={branchesLoading || authLoading || (assignedLoading && !branchIsAdmin) ? "Loading..." : "Select branch"} />
                  </SelectTrigger>
                  <SelectContent>
                    {availableBranches.map((branch) => (
                      <SelectItem key={branch.id} value={String(branch.id)}>
                        {branch.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            {navItems.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.path;

              return (
                <Link
                  key={item.id}
                  to={item.path}
                  onClick={() => setMobileMenuOpen(false)}
                  className={cn(
                    "flex items-center space-x-3 px-2 py-1.5 rounded-md text-base font-medium transition-colors min-h-[48px]",
                    isActive
                      ? "text-primary bg-primary/10"
                      : "text-muted-foreground hover:text-foreground hover:bg-muted",
                  )}
                >
                  <Icon className="w-5 h-5" />
                  <span>{item.label}</span>
                </Link>
              );
            })}
            <Link
              to="/notifications"
              onClick={() => setMobileMenuOpen(false)}
              className={cn(
                "flex items-center space-x-3 px-2 py-1.5 rounded-md text-base font-medium transition-colors min-h-[48px] relative",
                location.pathname === "/notifications"
                  ? "text-primary bg-primary/10"
                  : "text-muted-foreground hover:text-foreground hover:bg-muted",
              )}
            >
              <Bell className="w-5 h-5" />
              <span>Notifications</span>
              {unreadCount > 0 && (
                <Badge 
                  variant="destructive" 
                  className="ml-auto h-5 min-w-5 flex items-center justify-center px-1.5 text-[10px] rounded-full"
                >
                  {unreadCount > 99 ? "99+" : unreadCount}
                </Badge>
              )}
            </Link>
            <Button
              variant="ghost"
              size="default"
              onClick={() => {
                setMobileMenuOpen(false);
                signOut();
              }}
              className="w-full justify-start flex items-center space-x-3 px-2 py-1.5 min-h-[48px] text-base"
            >
              <LogOut className="w-5 h-5" />
              <span>Sign Out</span>
            </Button>
          </div>
        )}
      </div>
    </nav>
  );
};
